/* eslint-disable @typescript-eslint/no-shadow */
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import React, { useState } from 'react';
import { BsFillCaretRightFill } from 'react-icons/bs';
import { RiArrowDownSLine } from 'react-icons/ri';
import { NavLink } from 'react-router-dom';

import { useConferenceManagement } from '@/hooks/featureFlags/active-flags/useConferenceManagement';
import { NavigationV2 } from './v2/Navigation';

const sections: NavigationOption[] = [
  {
    name: 'Organizations',
    path: '/organizations',
  },
  {
    name: 'Users',
    path: '/users',
  },
  {
    name: 'Behavior Config',
    children: [
      { name: 'Behavior Config', path: '/behavior-config' },
      { name: 'QIP Metric Builder', path: '/behavior-config/qip-metric-builder/questions' },
      { name: 'Guidelines', path: '/behavior-config/guidelines' },
    ],
  },
];

const tools: NavigationOption[] = [
  {
    name: 'Reports',
    children: [{ name: 'QIP', path: '/reports/qip' }],
  },
];

interface NavigationOption {
  name: string;
  path?: string;
  children?: NavigationOption[];
}

export const Navigation = () => {
  const conferenceMangementFlag = useConferenceManagement();

  return (
    <>
      {conferenceMangementFlag ? (
        <NavigationV2 />
      ) : (
        <aside className="h-full p-3 border-r border-gray-300 shadow-lg bg-gray-50 w-72">
          <h3 className="text-sm font-bold tracking-wide text-gray-700 uppercase">sections</h3>
          <p className="ml-0.5 mb-3 text-sm">Get a list, add, edit, or delete</p>
          <ul className="mb-4 text-gray-800 border-b border-gray-200">
            {sections.map((route) => (
              <li key={route.name} className="border-b border-gray-200 last:border-0">
                {route.children ? (
                  <NavigationAccordion name={route.name} items={route.children} />
                ) : (
                  <NavigationLink name={route.name} path={route.path || ''} />
                )}
              </li>
            ))}
          </ul>
          <h3 className="text-sm font-bold tracking-wide text-gray-700 uppercase">tools</h3>
          <p className="ml-0.5 mb-2 text-sm">
            Tools to perform specific actions or ones that require multiple steps
          </p>
          <ul className="mb-4 text-gray-800 border-b border-gray-200">
            {tools.map((route) => (
              <li key={route.name} className="border-b border-gray-200 last:border-0">
                {route.children ? (
                  <NavigationAccordion name={route.name} items={route.children} />
                ) : (
                  <NavigationLink name={route.name} path={route.path || ''} />
                )}
              </li>
            ))}
          </ul>
        </aside>
      )}
    </>
  );
};

interface NavigationAccordionProps {
  name: string;
  items: NavigationOption[];
}

const NavigationAccordion = ({ name, items }: NavigationAccordionProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="w-full p-3 pl-1">
      <button onClick={() => setOpen((open) => !open)} className="w-full">
        <h4 className="flex items-center w-full font-semibold">
          <RiArrowDownSLine className={clsx('transition-all mr-2', open && 'rotate-180')} /> {name}
        </h4>
      </button>
      <Transition
        show={open}
        className="mt-1 overflow-hidden transition-all duration-300"
        enterFrom="max-h-0"
        enterTo="max-h-[200px]"
        leaveFrom="max-h-[200px]"
        leaveTo="max-h-0">
        <ul className="pl-8">
          {items.map((item) => (
            <li key={item.name}>
              <NavLink to={`${item.path || ''}`} className="py-2 text-sm">
                {item.name}
              </NavLink>
            </li>
          ))}
        </ul>
      </Transition>
    </div>
  );
};

interface NavigationLinkProps {
  path: string;
  name: string;
}

const NavigationLink = ({ path, name }: NavigationLinkProps) => (
  <NavLink
    to={path.toLocaleLowerCase()}
    className={({ isActive }) =>
      clsx(
        'w-full p-3 transition-all flex items-center font-semibold',
        isActive && 'bg-sky-700 text-white pl-5',
      )
    }
    data-cy={`nav-item-${path.replace('/', '')}`}>
    {({ isActive }) => (
      <>
        <BsFillCaretRightFill
          className={clsx(
            'text-xs transition-all',
            !isActive && 'text-gray-800 mr-2',
            isActive && 'text-white mr-4 -ml-2',
          )}
        />{' '}
        {name}
      </>
    )}
  </NavLink>
);
